import { forwardRef, useImperativeHandle, useRef } from 'react';
import { BackSide, Color, Mesh, ShaderMaterial, Uniform } from 'three';

import fragmentShader from './sky.frag';
import vertexShader from './sky.vert';

export interface CustomSkyProps {
  radius?: number;
  horizonColor: Color;
  skyGroundColor: Color;
}

export interface CustomSkyRef {
  horizonColor: Color;
  skyGroundColor: Color;
  visible: boolean;
}

export const CustomSky = forwardRef<CustomSkyRef, CustomSkyProps>(
  ({ radius = 3500, horizonColor, skyGroundColor }, ref) => {
    const materialRef = useRef<ShaderMaterial>(
      new ShaderMaterial({
        vertexShader,
        fragmentShader,
        side: BackSide,
        uniforms: {
          uHorizonColor: new Uniform(horizonColor),
          uSkyGroundColor: new Uniform(skyGroundColor),
        },
      })
    );
    const meshRef = useRef<Mesh>(null);

    useImperativeHandle(ref, () => ({
      get horizonColor() {
        return materialRef.current.uniforms['uHorizonColor'].value;
      },
      get skyGroundColor() {
        return materialRef.current.uniforms['uSkyGroundColor'].value;
      },
      get visible() {
        return materialRef.current.uniforms['uHorizonColor'].value;
      },

      set horizonColor(color: Color) {
        materialRef.current.uniforms['uHorizonColor'].value = color;
      },
      set skyGroundColor(color: Color) {
        materialRef.current.uniforms['uSkyGroundColor'].value = color;
      },
      set visible(isVisible: boolean) {
        if (!meshRef.current) return;
        meshRef.current.visible = isVisible;
      },
    }));

    return (
      <mesh ref={meshRef}>
        <sphereGeometry args={[radius, 32, 32]} />
        <primitive object={materialRef.current} attach="material" />
      </mesh>
    );
  }
);
CustomSky.displayName = 'CustomSky';
